import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { AlertCircle, Download, CheckCircle } from "lucide-react";
import { Alert, AlertDescription } from "../components/ui/alert";
import { useTranslation } from "../i18n";

export default function DownloadPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [downloadStarted, setDownloadStarted] = useState<Set<number>>(new Set());

  // Validate token and get download links
  const { data: downloadData, isLoading, error } = trpc.downloadTokens.getDownloadLinks.useQuery(
    { token: token || "" },
    { enabled: !!token }
  );

  const handleDownload = (url: string, id: number) => {
    window.open(url, "_blank");
    setDownloadStarted((prev) => new Set(prev).add(id));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-2xl mx-4">
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (error || !downloadData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-2xl mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {t.downloads.errorTitle}
            </CardTitle>
            <CardDescription>
              {error?.message || t.downloads.invalidOrExpiredLink}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              Your download link may have expired or is invalid. Please contact support if you need assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const expiresAt = new Date(downloadData.expiresAt);
  const isExpired = expiresAt < new Date();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-6 w-6" />
              Your Files Are Ready
            </CardTitle>
            <CardDescription>
              Order #{downloadData.orderNumber} - {downloadData.projectTitle}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Info */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Customer:</span>
                <span>{downloadData.customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Project:</span>
                <span>{downloadData.projectTitle}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Files:</span>
                <span>{downloadData.downloadLinks.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Expires:</span>
                <span className={isExpired ? "text-red-600" : ""}>
                  {expiresAt.toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Expiry Warning */}
            {!isExpired && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This download link will expire on{" "}
                  <strong>{expiresAt.toLocaleDateString()}</strong>. Please download all
                  files before then.
                </AlertDescription>
              </Alert>
            )}

            {isExpired && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This download link has expired. Please contact support for assistance.
                </AlertDescription>
              </Alert>
            )}

            {/* Download Links */}
            {!isExpired && (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Download Files:</h3>
                <div className="grid gap-3">
                  {downloadData.downloadLinks.map((link) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-3">
                        {link.type === "image" ? (
                          <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center text-blue-600">
                            📷
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-purple-100 rounded flex items-center justify-center text-purple-600">
                            🎥
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{link.filename}</p>
                          <p className="text-xs text-gray-500">
                            {link.type === "image" ? t.common.image : t.common.video}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDownload(link.url, link.id)}
                        variant={downloadStarted.has(link.id) ? "outline" : "default"}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {downloadStarted.has(link.id) ? t.common.downloaded : t.common.download}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Download All Button */}
            {!isExpired && downloadData.downloadLinks.length > 1 && (
              <div className="pt-4 border-t">
                <Button
                  onClick={() => {
                    downloadData.downloadLinks.forEach((link) => {
                      setTimeout(() => handleDownload(link.url, link.id), 100);
                    });
                  }}
                  className="w-full"
                  size="lg"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download All Files
                </Button>
                <p className="text-xs text-center text-gray-500 mt-2">
                  Note: Your browser may ask permission to download multiple files
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Support Info */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              If you encounter any issues downloading your files or if your link has expired,
              please contact support with your order number:{" "}
              <strong>{downloadData.orderNumber}</strong>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
